import { FunctionComponent, RefObject } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '../../../../common/styles/styles.utils';
import { StorySeekBarProgress } from './storySeekBarProgress/storySeekBarProgress';
import { resumeVideo, setSelectedScene } from '../../store/controlsPlugin.actions';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { useStyles } from '../../../../common/hooks/useStyles';
import { getStorySeekBarsStyleObject } from './storySeekBar.style';
import { VideoTagStatusType } from '../../../../common/store/store.types';

interface StorySeekBarProps {
  index: number;
  currentStoryDuration: number;
}

export const StorySeekBar: FunctionComponent<StorySeekBarProps> = props => {
  const { index, currentStoryDuration } = props;
  const dispatch = useDispatch();
  const playerSize = useSelector(playerDataSelector.playerSize);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);

  const storyBarRef: RefObject<HTMLDivElement> = useRef();
  const [videoStatusBeforeDragging, setVideoStatusBeforeDragging] = useState<VideoTagStatusType>(videoTagStatus);

  const styles = useStyles(getStorySeekBarsStyleObject, [playerSize]);
  const { storyStyle, clickableStoryWrapperStyle } = styles;

  const getOffsetX = (mouseEvent: Event, isTouchEvent = false): number => {
    if (isTouchEvent) {
      return (mouseEvent as TouchEvent).changedTouches[0].clientX - storyBarRef.current.getBoundingClientRect().left;
    }
    return (mouseEvent as MouseEvent).clientX - storyBarRef.current.getBoundingClientRect().left;
  };

  const onStoryClicked = (mouseEvent: Event, isTouchEvent = false): void => {
    const percentOfSeekBar = getOffsetX(mouseEvent, isTouchEvent) / storyBarRef.current.clientWidth;
    dispatch(setSelectedScene(index, percentOfSeekBar));
  };

  const onMouseUp = (): void => {
    if (videoStatusBeforeDragging === 'playing') {
      dispatch(resumeVideo());
    }
  };

  const onTouchEnd = (): void => {
    if (videoStatusBeforeDragging === 'playing') {
      dispatch(resumeVideo());
    }
  };

  const onMouseDown = (mouseEvent: MouseEvent): void => {
    onStoryClicked(mouseEvent, false);
    setVideoStatusBeforeDragging(videoTagStatus);
  };

  const onTouchStart = (touchEvent: TouchEvent): void => {
    onStoryClicked(touchEvent, true);
    setVideoStatusBeforeDragging(videoTagStatus);
  };

  return (
    <div style={clickableStoryWrapperStyle} onMouseDown={onMouseDown} onTouchStart={onTouchStart} onMouseUp={onMouseUp} onTouchEnd={onTouchEnd} ref={storyBarRef}>
      <div style={css(storyStyle)}>
        <StorySeekBarProgress index={index} currentStoryDuration={currentStoryDuration} />
      </div>
    </div>
  );
};
